import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  FileText, CreditCard, Clock, CheckCircle, ArrowRight, PieChart, BarChart, Users, List 
} from 'lucide-react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

// --- Data Interfaces ---
interface Stat {
  value: number | string;
  label: string;
  icon: React.ElementType;
  color: string;
}
interface RecentQuote {
  _id: string; reference: string; title: string; status: string; total: number; createdAt: string;
}
interface RecentInvoice {
  _id: string; invoiceNumber: string; status: string; totalAmount: number; dueDate: string;
}
interface RecentPayment {
  _id: string; paymentNumber: string; amount: number; paymentDate: string; status: string;
}

interface DashboardData {
  stats: {
    pendingQuotes: number;
    acceptedQuotes: number;
    unpaidInvoices: number;
    totalPaid: number;
  };
  charts: {
    monthlyInstallations: {name: string, value: number}[];
    typeBreakdown: {name: string, value: number}[];
    techBreakdown: {name: string, value: number}[];
    statusBreakdown: {name: string, value: number}[];
  };
  recentQuotes: RecentQuote[];
  recentInvoices: RecentInvoice[];
  recentPayments: RecentPayment[];
}

// --- Helper Components ---
const StatCard: React.FC<{ stat: Stat }> = ({ stat }) => (
  <Card className="p-6">
    <div className="flex items-center">
      <div className={`p-3 rounded-full mr-4 ${stat.color}`}>
        <stat.icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
        <p className="text-sm font-medium text-gray-500">{stat.label}</p>
      </div>
    </div>
  </Card>
);

const InfoCard: React.FC<{ title: string; children: React.ReactNode; buttonLink: string; }> = ({ title, children, buttonLink }) => {
  const navigate = useNavigate();
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
      <Button variant="outline" size="sm" className="mt-6 w-full" onClick={() => navigate(buttonLink)}>
        Voir tout <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </Card>
  );
};

const SimpleBarChart = ({ data, title, colorClass }: { data: {name: string, value: number}[], title: string, colorClass: string }) => {
  const maxValue = Math.max(...data.map(d => d.value), 0);
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-4">
        {data.map(item => (
          <div key={item.name} className="grid grid-cols-3 gap-2 items-center">
            <p className="text-sm text-gray-600 truncate col-span-1">{item.name}</p>
            <div className="col-span-2 bg-gray-200 rounded-full h-6">
              <div 
                className={`${colorClass} h-6 rounded-full flex items-center justify-end pr-2`} 
                style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
              >
                <span className="text-sm font-medium text-white">{item.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const BreakdownList = ({ data, title, icon: Icon }: { data: {name: string, value: number}[], title: string, icon: React.ElementType }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map(item => (
          <div key={item.name} className="flex justify-between items-center text-sm">
            <div className="flex items-center">
              <Icon className="w-4 h-4 mr-2 text-gray-400" />
              <p className="text-gray-700">{item.name}</p>
            </div>
            <p className="font-semibold">{total > 0 ? ((item.value / total) * 100).toFixed(0) : 0}% ({item.value})</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

const getStatusBadge = (status: string, type: 'quote' | 'invoice') => {
  const config: { [key: string]: { label: string; color: string } } = {
    // Quote statuses
    sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
    accepted: { label: 'Accepté', color: 'bg-green-100 text-green-800' },
    // Invoice/Payment statuses
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
    overdue: { label: 'En retard', color: 'bg-red-100 text-red-800' },
    paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
    completed: { label: 'Payé', color: 'bg-green-100 text-green-800' },
  };
  const { label, color } = config[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  return <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${color}`}>{label}</span>;
};

// --- Main Dashboard Component ---

const ClientDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        console.log('Fetching client dashboard data...');
        const response = await apiService.dashboard.getClientDashboard();
        console.log('Dashboard data received:', response.data);
        setData(response.data.data);
      } catch (error) {
        console.error('--- DASHBOARD FETCH FAILED ---', error);
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        }
        toast.error('Erreur lors du chargement des données du tableau de bord.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return <Layout><div>Chargement...</div></Layout>;
  }
  if (!data) {
    return <Layout><div>Impossible de charger les données du tableau de bord.</div></Layout>;
  }

  const { stats, charts, recentQuotes, recentInvoices, recentPayments } = data;

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
          <p className="text-gray-600 mt-1">Aperçu de vos activités et statistiques.</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard stat={{ value: stats.pendingQuotes, label: 'Devis en attente', icon: Clock, color: 'bg-yellow-500' }} />
          <StatCard stat={{ value: stats.unpaidInvoices, label: 'Factures impayées', icon: FileText, color: 'bg-red-500' }} />
          <StatCard stat={{ value: stats.acceptedQuotes, label: 'Projets acceptés', icon: CheckCircle, color: 'bg-green-500' }} />
          <StatCard stat={{ value: stats.totalPaid.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }), label: 'Total payé', icon: CreditCard, color: 'bg-blue-500' }} />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <SimpleBarChart data={charts.monthlyInstallations} title="Installations par mois" colorClass="bg-blue-500" />
          <SimpleBarChart data={charts.techBreakdown} title="Installations par technicien" colorClass="bg-purple-500" />
          <BreakdownList data={charts.typeBreakdown} title="Répartition par type" icon={PieChart} />
          <BreakdownList data={charts.statusBreakdown} title="Statut des installations" icon={List} />
        </div>

        {/* Recent Items Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <InfoCard title="Devis Récents" buttonLink="/client/quotes">
            {recentQuotes.length > 0 ? recentQuotes.map(q => (
              <div key={q._id} className="flex justify-between items-center text-sm">
                <div>
                  <p className="font-medium text-gray-800">{q.reference} - {q.title}</p>
                  <p className="text-gray-500">{q.total.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                </div>
                {getStatusBadge(q.status, 'quote')}
              </div>
            )) : <p className="text-sm text-gray-500">Aucun devis récent.</p>}
          </InfoCard>

          <InfoCard title="Factures Récentes" buttonLink="/client/invoices">
            {recentInvoices.length > 0 ? recentInvoices.map(i => (
              <div key={i._id} className="flex justify-between items-center text-sm">
                <div>
                  <p className="font-medium text-gray-800">{i.invoiceNumber}</p>
                  <p className="text-gray-500">{i.totalAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                </div>
                {getStatusBadge(i.status, 'invoice')}
              </div>
            )) : <p className="text-sm text-gray-500">Aucune facture récente.</p>}
          </InfoCard>

          <InfoCard title="Paiements Récents" buttonLink="/client/payments">
            {recentPayments.length > 0 ? recentPayments.map(p => (
              <div key={p._id} className="flex justify-between items-center text-sm">
                <div>
                  <p className="font-medium text-gray-800">{p.paymentNumber || p._id.slice(-6)}</p>
                  <p className="text-gray-500">{new Date(p.paymentDate).toLocaleDateString('fr-FR')}</p>
                </div>
                <p className="font-semibold text-gray-800">{p.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
              </div>
            )) : <p className="text-sm text-gray-500">Aucun paiement récent.</p>}
          </InfoCard>
        </div>
      </div>
    </Layout>
  );
};

export default ClientDashboard;